import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import CarrierHealthBanner from "@/components/CarrierHealthBanner";
import ActionItemsList from "@/components/ActionItemsList";
import {
  CheckCircle2, AlertTriangle, Clock3, FileWarning, ArrowRight,
  ShieldCheck, Calendar, Truck, ListChecks
} from "lucide-react";

const STATUS_MAP = {
  active: { tag: "hs-tag-success", icon: CheckCircle2, label: "ACTIVE" },
  filed: { tag: "hs-tag-success", icon: CheckCircle2, label: "FILED" },
  in_progress: { tag: "hs-tag-warn", icon: Clock3, label: "IN PROGRESS" },
  pending: { tag: "hs-tag-warn", icon: Clock3, label: "PENDING" },
  not_started: { tag: "hs-tag-info", icon: Clock3, label: "NOT STARTED" },
  expired: { tag: "hs-tag-danger", icon: AlertTriangle, label: "EXPIRED" },
};

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export default function ClientDashboard() {
  const { user } = useAuth();
  const [filings, setFilings] = useState([]);
  const [docs, setDocs] = useState([]);
  const [safety, setSafety] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, b, s] = await Promise.all([
          api.get("/filings"),
          api.get("/documents"),
          api.get("/safety/score"),
        ]);
        setFilings(a.data); setDocs(b.data); setSafety(s.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const expiringSoon = filings.filter((f) => {
    const d = daysUntil(f.expires_at);
    return d !== null && d <= 30 && d >= 0;
  });
  const pendingDocs = docs.filter((d) => d.status === "pending");
  const rejectedDocs = docs.filter((d) => d.status === "rejected");

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      {/* HERO: Carrier Health — answers "Am I in the clear?" in one glance */}
      <CarrierHealthBanner />

      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="hs-overline text-blue-700">DRIVER PORTAL · OVERVIEW</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-2">
            Welcome,<br/>{user?.company_name || (user?.name || "Operator").split(" ")[0]}
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            {user?.name && <>{user.name} · </>}DOT {user?.dot_number || "—"} · MC {user?.mc_number || "—"}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Link to="/documents" className="hs-btn-secondary min-h-[48px] inline-flex items-center" data-testid="dashboard-upload-cta">
            UPLOAD DOCUMENT
          </Link>
          <button
            onClick={async () => {
              try {
                const res = await api.get("/reports/compliance.pdf", { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                window.open(url, "_blank");
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (e) {
                if (e?.response?.status === 402) {
                  toast.error("This PDF report is not available. Please contact your compliance team.", { duration: 5000 });
                } else {
                  toast.error("Could not generate PDF");
                }
              }
            }}
            className="hs-btn-primary"
            data-testid="dashboard-pdf-cta"
          >
            DOWNLOAD PDF REPORT
          </button>
        </div>
      </div>

      {/* Status grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {filings.map((f) => {
          const s = STATUS_MAP[f.status] || STATUS_MAP.pending;
          const Icon = s.icon;
          const dleft = daysUntil(f.expires_at);
          return (
            <div key={f.id} className="hs-card hs-card-hover p-5" data-testid={`status-card-${f.type}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="hs-overline">{f.type}</div>
                  <div className="font-display font-bold text-lg mt-1">{f.label}</div>
                </div>
                <Icon size={22} className={
                  f.status === "active" || f.status === "filed" ? "text-emerald-600" :
                  f.status === "expired" ? "text-red-600" : "text-amber-600"
                }/>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className={`hs-tag ${s.tag}`}>{s.label}</span>
                {f.expires_at && (
                  <span className="text-xs font-mono text-slate-500">
                    {dleft !== null && dleft >= 0 ? `${dleft}d left` : "expired"}
                  </span>
                )}
              </div>
              {f.notes && <p className="mt-3 text-xs text-slate-500 line-clamp-2">{f.notes}</p>}
            </div>
          );
        })}
      </div>

      {/* Unified action-items widget — merges expiring filings, driver expirations, severe violations */}
      <ActionItemsList />

      {/* Documents needing eyes (small ancillary cards) */}
      {(pendingDocs.length > 0 || rejectedDocs.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {pendingDocs.length > 0 && (
            <AlertCard
              title="DOCUMENTS PENDING REVIEW"
              icon={Clock3}
              count={pendingDocs.length}
              accent="info"
              items={pendingDocs.slice(0, 4).map((d) => ({
                id: d.id, label: d.filename, sub: d.category, href: "/documents",
              }))}
              empty="All documents reviewed."
              testid="alert-pending-docs"
            />
          )}
          {rejectedDocs.length > 0 && (
            <AlertCard
              title="ATTENTION REQUIRED"
              icon={FileWarning}
              count={rejectedDocs.length}
              accent="danger"
              items={rejectedDocs.slice(0, 4).map((d) => ({
                id: d.id, label: d.filename, sub: d.notes || d.category, href: "/documents",
              }))}
              empty="No rejected documents."
              testid="alert-rejected-docs"
            />
          )}
        </div>
      )}

      {/* Internal compliance + next deadline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Link to="/safety" className="hs-card hs-card-hover p-5 lg:col-span-2 flex items-center gap-6 group" data-testid="dashboard-safety-card">
          <SafetyMini value={safety?.score ?? 0} grade={safety?.grade ?? "—"} />
          <div className="flex-1 min-w-0">
            <div className="hs-overline">INTERNAL COMPLIANCE SCORE</div>
            <div className="font-display font-bold text-xl mt-1">From your logged data</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <MiniStat label="Inspections 12mo" value={safety?.stats?.inspections_12mo ?? 0} />
              <MiniStat label="Grounded (OOS)" title="Out-of-Service inspections in last 12 months" value={safety?.stats?.oos_inspections ?? 0} bad={!!(safety?.stats?.oos_inspections)} />
              <MiniStat label="Open Tickets" title="Open violations" value={safety?.stats?.open_violations ?? 0} bad={!!(safety?.stats?.open_violations)} />
              <MiniStat label="Severe Citations" value={safety?.stats?.severe_violations ?? 0} bad={!!(safety?.stats?.severe_violations)} />
            </div>
          </div>
          <ArrowRight size={16} className="text-slate-400 group-hover:text-blue-700"/>
        </Link>
        <Stat icon={Calendar} label="NEXT DEADLINE" value={expiringSoon[0] ? `${daysUntil(expiringSoon[0].expires_at)}D · ${expiringSoon[0].type}` : "—"} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Truck} label="ACTIVE FILINGS" value={filings.filter(f => f.status === "active" || f.status === "filed").length} />
        <Stat icon={ListChecks} label="TOTAL FILINGS" value={filings.length} />
        <Stat icon={Clock3} label="OPEN ITEMS" value={pendingDocs.length + filings.filter(f => f.status === "in_progress" || f.status === "pending").length} />
        <Stat icon={ShieldCheck} label="DOCUMENTS" value={`${docs.length} ON FILE`} />
      </div>

      {loading && <div className="hs-overline">LOADING…</div>}
    </div>
  );
}

function SafetyMini({ value, grade }) {
  const r = 36, c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  const color = value >= 85 ? "#059669" : value >= 70 ? "#D97706" : "#DC2626";
  return (
    <div className="relative w-[100px] h-[100px] flex-shrink-0">
      <svg width="100" height="100" className="-rotate-90">
        <circle cx="50" cy="50" r={r} stroke="#E2E8F0" strokeWidth="8" fill="transparent"/>
        <circle cx="50" cy="50" r={r} stroke={color} strokeWidth="8" fill="transparent"
                strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 0.6s ease" }}/>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-black text-2xl" style={{ color }} data-testid="dashboard-safety-score">{value}</div>
        <div className="font-mono text-[10px] tracking-widest text-slate-500">GRADE {grade}</div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, bad = false, title }) {
  return (
    <div className="border border-slate-200 px-2 py-1.5" title={title}>
      <div className="hs-overline text-[9px]">{label}</div>
      <div className={`font-display font-extrabold text-2xl tracking-tighter ${bad ? "text-amber-600" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function AlertCard({ title, icon: Icon, count, items, empty, accent, testid }) {
  const ringColor = accent === "danger" ? "#DC2626" : accent === "warn" ? "#D97706" : accent === "info" ? "#2563EB" : "#1D4ED8";
  return (
    <div className="hs-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={16} style={{ color: ringColor }} />
          <span className="hs-overline">{title}</span>
        </div>
        <span className="font-mono font-bold text-2xl" style={{ color: ringColor }}>
          {String(count).padStart(2, "0")}
        </span>
      </div>
      <div className="mt-4 space-y-2">
        {items.length === 0 && (
          <p className="text-xs text-slate-500">{empty}</p>
        )}
        {items.map((it) => (
          <Link key={it.id} to={it.href} className="flex items-start justify-between gap-3 py-2 border-t border-slate-200 hover:bg-slate-100 -mx-2 px-2">
            <div className="min-w-0">
              <div className="text-sm truncate">{it.label}</div>
              <div className="text-xs text-slate-500 truncate">{it.sub}</div>
            </div>
            <ArrowRight size={14} className="text-slate-500 flex-shrink-0 mt-1" />
          </Link>
        ))}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="hs-card p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-blue-700" />
        <span className="hs-overline">{label}</span>
      </div>
      <div className="font-display font-extrabold text-3xl md:text-4xl tracking-tighter mt-2 text-slate-900">{value}</div>
    </div>
  );
}
